import refreshIcon from '@/icons/refresh-icon.svg';
import sparkleIcon from '@/icons/sparkle-icon-deep-ocean-blue.svg';
import {
  getProductSelection,
  selectOption,
  selectPlan,
} from '@/product/product-selection';
import {PACK_SIZE} from '@/product/product-selection/pack-size';
import {intervalFrequency, intervalUnit} from '@/selling-plan/utilities';
import {capitalize} from '@/utilities/capitalize';
import plural from '@/utilities/plural';
import {batch} from '@preact/signals';
import {Plan} from '@rechargeapps/storefront-client';
import {useEffect, useState} from 'preact/hooks';
import DeliverySelectorLabel from './DeliverySelectorLabel';
import FrequencyHelperButtonGroup from './FrequencyHelperButtonGroup';
import PlanSelectorButton from './PlanSelectorButton';
import SelectorItemRadioBox from './SelectorItemRadioBox';
import SubscribeAndSaveLabel from './SubscribeAndSaveLabel';

const subscribeBadgeLabel = 'Best Value';
const percentage = 40;
const subPrice = 12;
const subComparePrice = 20;
const deliveryFrequencyLabel = 'Delivery Frequency';
const everyLabel = 'EVERY';
const sparkleIconAlt = 'Sparkle Icon';
const refreshIconAlt = 'Delivery Frequency Icon';

export type PlanSelectorProps = {
  currency?: string;
  locale?: string;
  formatMoneyWithDecimals?: boolean;
};

export default function PlanSelector({
  currency = 'USD',
  locale,
  formatMoneyWithDecimals,
}: PlanSelectorProps) {
  const {selectedVariant, selectedPlan, packOptions, monthlyPlans} =
    getProductSelection();

  // Get color settings for the selected variant.
  const selectedColor = selectedVariant?.accentColor ?? '';

  const [selectedMonthlyPlan, setSelectedMonthlyPlan] = useState<Plan | null>(
    null
  );

  // Ensure the local state is in sync with the global state.
  useEffect(() => {
    if (selectedPlan && monthlyPlans.includes(selectedPlan)) {
      setSelectedMonthlyPlan(selectedPlan);
    } else {
      setSelectedMonthlyPlan(monthlyPlans[0] ?? null);
    }
  }, [monthlyPlans, selectedPlan]);

  /**
   * Handles the click event for 'Subscribe & Save' button.
   * Updates the global product selection state in two ways:
   * - Selects the one pack variant
   * - Selects the monthly plan
   */
  const handleClickSubscribe = () => {
    batch(() => {
      if (packOptions.length > 0) {
        selectOption({name: PACK_SIZE, value: packOptions[0]});
      }

      if (selectedMonthlyPlan) {
        selectPlan(selectedMonthlyPlan);
      }
    });
  };

  /**
   * Handles the click event for selecting a subscription frequency.
   * Updates the local and global selection state.
   *
   * @param plan - The selected monthly plan.
   * @returns void
   */
  const handleClickMonthlyPlan = (plan: Plan): void => {
    setSelectedMonthlyPlan(plan);
    selectPlan(plan);
  };

  return (
    <SelectorItemRadioBox
      color={selectedColor}
      isSelected={!!selectedPlan && monthlyPlans.includes(selectedPlan)}
      onClick={handleClickSubscribe}
      dataTestid="recurring-purchase"
      badgeLabel={
        <span>
          <img
            src={sparkleIcon}
            alt={sparkleIconAlt}
            className="mr-1 inline size-4 align-baseline"
          />
          {subscribeBadgeLabel}
        </span>
      }
      label={
        <DeliverySelectorLabel
          compareAtPrice={subComparePrice}
          currency={currency}
          locale={locale}
          price={subPrice}
          formatMoneyWithDecimals={formatMoneyWithDecimals}
        >
          <SubscribeAndSaveLabel
            color={selectedColor}
            percentage={percentage}
          />
        </DeliverySelectorLabel>
      }
    >
      <div className="flex flex-col gap-y-2">
        <span className="text-xs font-medium">
          <img
            src={refreshIcon}
            alt={refreshIconAlt}
            className="mr-1 inline size-4.5 align-middle"
          />
          {deliveryFrequencyLabel}
        </span>
        <div className="grid grid-cols-4 gap-2">
          {monthlyPlans.map((plan) => {
            const frequency = intervalFrequency(plan);
            const unit = intervalUnit(plan);
            return (
              <PlanSelectorButton
                bottom={unit ? capitalize(plural(frequency, unit)) : null}
                color={selectedColor}
                dataTestId={`order-interval-${frequency}`}
                isSelected={plan === selectedMonthlyPlan}
                key={plan.id}
                middle={frequency.toString()}
                onClick={() => handleClickMonthlyPlan(plan)}
                top={everyLabel}
              />
            );
          })}
        </div>
        <FrequencyHelperButtonGroup />
      </div>
    </SelectorItemRadioBox>
  );
}
